import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common';
import { CHECK_STOCK_ORDER_STATUSES } from '../constants/internalOrdersConstants';
import {
  buildArticlesByUuid,
  formatOrderItemStatus,
  getArticleLabel,
  canCancelExternalOrder,
  getInternalOrderApiErrorMessage,
  getProcessResultMessage,
} from '../utils/internalOrdersUtils';
import SupplyOrderActionDialog from '../../supply-orders/components/SupplyOrderActionDialog';
import { useInventoryArticlesByUuids } from '../hooks/useInventoryArticlesByUuids';
import { useConsumptionsByOrder, useConfirmConsumption } from '../../../hooks/useConsumptions';
import { useWarehouses } from '../../../hooks/useWarehouses';
import GenerateConsumptionDialog from '../../consumptions/components/GenerateConsumptionDialog';
import GenerateSupplyOrdersDialog from '../../supply-orders/components/GenerateSupplyOrdersDialog';
import { useCreateSupplyOrdersFromExternalOrder } from '../../../hooks/useSupplyOrders';
import { useProviders } from '../../../hooks/useProviders';
import {
  canGenerateSupplyOrdersFromExternalOrder,
} from '../../supply-orders/utils/supplyOrdersUtils';
import {
  canGenerateConsumptionFromOrder,
  formatConsumptionQuantity,
  formatConsumptionStatus,
  getActiveConsumption,
  getBlockingConsumption,
  getGenerateConsumptionButtonLabel,
  getItemConsumableQuantity,
  getReadyToConsumeOrderItems,
  getConsumptionApiErrorMessage,
} from '../../consumptions/utils/consumptionsUtils';
import { CONSUMPTION_STATUSES } from '../../consumptions/constants/consumptionsConstants';
import { usePermissions } from '../../../hooks/usePermissions';
import { guardDialogClose, isAnyActionPending } from '../../utils/actionLockUtils';
import InternalOrderStatusChip from './InternalOrderStatusChip';

const InternalOrderDetailDialog = ({
  open,
  onClose,
  order,
  onAuthorizeOrder,
  onCheckStock,
  onSetConsumptionAuthorization,
  onCancelOrder,
  onOrderUpdated,
  isProcessing = false,
  isLoading = false,
}) => {
  const [resultMessage, setResultMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateSupplyDialogOpen, setGenerateSupplyDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [nestedActionPending, setNestedActionPending] = useState(false);

  const { permissions } = usePermissions();
  const createSupplyOrdersMutation = useCreateSupplyOrdersFromExternalOrder();

  const articleUuids = useMemo(
    () => (order?.items || []).map((item) => item.uuid_inventory_article),
    [order?.items]
  );

  const { articles: inventoryArticles } = useInventoryArticlesByUuids(
    articleUuids,
    open && Boolean(order)
  );

  const {
    data: orderConsumptions = [],
    refetch: refetchOrderConsumptions,
  } = useConsumptionsByOrder(order?.uuid, {
    enabled: open && Boolean(order?.uuid),
  });

  const confirmConsumptionMutation = useConfirmConsumption();
  const isActionLocked = isAnyActionPending(
    isProcessing,
    confirmConsumptionMutation.isPending,
    createSupplyOrdersMutation.isPending,
    nestedActionPending
  );

  const { data: warehouses = [] } = useWarehouses({
    enabled: generateDialogOpen || generateSupplyDialogOpen,
  });

  const { data: providers = [] } = useProviders({
    enabled: generateSupplyDialogOpen,
  });

  useEffect(() => {
    if (open) {
      setResultMessage('');
      setErrorMessage('');
    }
  }, [open, order?.uuid]);

  const articlesByUuid = useMemo(
    () => buildArticlesByUuid(inventoryArticles),
    [inventoryArticles]
  );

  const canCheckStock =
    permissions.canCheckExternalOrderStock &&
    order &&
    CHECK_STOCK_ORDER_STATUSES.includes(order.status);
  const canAuthorizeOrder =
    order?.status === 'pending_authorization' && permissions.canAuthorizeTechnicalOrders;
  const canAuthorizeConsumption =
    order &&
    permissions.canAuthorizeConsumption &&
    !order.consumption_authorized &&
    order.status !== 'pending_authorization' &&
    !['consumed', 'cancelled', 'failed'].includes(order.status);
  const canGenerateConsumption =
    permissions.canGenerateConsumptionFromOrder &&
    canGenerateConsumptionFromOrder(order, orderConsumptions);
  const generateConsumptionLabel = getGenerateConsumptionButtonLabel(order);
  const readyToConsumeItems = getReadyToConsumeOrderItems(order);
  const pendingOrderAuthorization = order?.status === 'pending_authorization';
  const pendingConsumptionAuthorization =
    !pendingOrderAuthorization &&
    readyToConsumeItems.length > 0 &&
    !order?.consumption_authorized;
  const blockingConsumption = getBlockingConsumption(orderConsumptions);
  const blockedByPendingConsumption =
    readyToConsumeItems.length > 0 &&
    order?.consumption_authorized &&
    Boolean(blockingConsumption);
  const canConfirmBlockingConsumption =
    permissions.canConfirmConsumptions &&
    blockingConsumption?.status === CONSUMPTION_STATUSES.GENERATED;
  const canGenerateSupplyOrders =
    permissions.canCreateSupplyOrderDraftFromExternalOrder &&
    canGenerateSupplyOrdersFromExternalOrder(order);
  const activeConsumption = getActiveConsumption(orderConsumptions);
  const canCancelOrder = canCancelExternalOrder(order, permissions);

  const handleCheckStock = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      const result = await onCheckStock(order.uuid);
      setResultMessage(getProcessResultMessage(result));
    } catch (error) {
      setErrorMessage(getInternalOrderApiErrorMessage(error, 'No se pudo controlar el stock'));
    }
  };

  const handleAuthorizeOrder = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      await onAuthorizeOrder(order.uuid);
      await onOrderUpdated?.();
      setResultMessage('Pedido técnico autorizado correctamente.');
    } catch (error) {
      setErrorMessage(getInternalOrderApiErrorMessage(error, 'No se pudo autorizar el pedido'));
    }
  };

  const handleAuthorizeConsumption = async () => {
    if (!order?.uuid) return;

    try {
      setErrorMessage('');
      await onSetConsumptionAuthorization(order.uuid, true);
      await onOrderUpdated?.();
      setResultMessage('Consumo autorizado correctamente.');
    } catch (error) {
      setErrorMessage(getInternalOrderApiErrorMessage(error, 'No se pudo autorizar el consumo'));
    }
  };

  const handleGenerateSuccess = async () => {
    await refetchOrderConsumptions();
    await onOrderUpdated?.();
    setResultMessage('Consumo generado correctamente.');
  };

  const handleConfirmBlockingConsumption = async () => {
    if (!blockingConsumption?.uuid) return;

    try {
      setErrorMessage('');
      await confirmConsumptionMutation.mutateAsync({ id: blockingConsumption.uuid });
      await refetchOrderConsumptions();
      await onOrderUpdated?.();
      setResultMessage(
        `Consumo ${blockingConsumption.consumption_number} confirmado. Ya podés generar el consumo pendiente.`
      );
    } catch (error) {
      setErrorMessage(
        getConsumptionApiErrorMessage(error, 'No se pudo confirmar el consumo activo')
      );
    }
  };

  const handleGenerateSupplyOrders = async (params) => {
    const result = await createSupplyOrdersMutation.mutateAsync(params);
    await onOrderUpdated?.();
    setResultMessage(
      `Se generaron ${Array.isArray(result) ? result.length : 1} orden(es) a proveedor.`
    );
    return result;
  };

  if (!open) return null;

  if (isLoading || !order) {
    return (
      <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
        <DialogTitle>Detalle del pedido</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isActionLocked, onClose)}
      size="large"
      minHeight="60vh"
    >
      <DialogTitle>
        Pedido {order.number}
      </DialogTitle>

      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {resultMessage && <Alert severity="info">{resultMessage}</Alert>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InternalOrderStatusChip status={order.status} label={order.status_label} />
            <Chip
              label={`Consumo: ${order.consumption_authorized ? 'autorizado' : 'sin autorizar'}`}
              color={order.consumption_authorized ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip label={`Depósito: ${order.warehouse_label || '-'}`} variant="outlined" />
            <Chip label={`Proyecto: ${order.project_label || '-'}`} variant="outlined" />
            <Chip label={`Creado por: ${order.created_by_label || '-'}`} variant="outlined" />
            <Chip label={`Necesidad: ${order.requested_at_label || '-'}`} variant="outlined" />
          </Box>

          {order.observations && (
            <Typography variant="body2" color="text.secondary">
              Observaciones: {order.observations}
            </Typography>
          )}

          {order.status === 'cancelled' && order.cancellation_reason && (
            <Alert severity="warning">
              Motivo de cancelación: {order.cancellation_reason}
            </Alert>
          )}

          <Typography variant="subtitle1" fontWeight={700}>
            Materiales solicitados
          </Typography>

          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell>Artículo</HeadTableCell>
                  <HeadTableCell>Solicitado</HeadTableCell>
                  <HeadTableCell>Faltante</HeadTableCell>
                  <HeadTableCell>A consumir</HeadTableCell>
                  <HeadTableCell>Estado</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(order.items || []).length === 0 ? (
                  <TableRow>
                    <RowTableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                      Sin materiales cargados
                    </RowTableCell>
                  </TableRow>
                ) : (
                  (order.items || []).map((item) => (
                    <TableRow key={item.uuid}>
                      <RowTableCell>
                        {getArticleLabel(articlesByUuid, item.uuid_inventory_article)}
                      </RowTableCell>
                      <RowTableCell>{item.requested_quantity}</RowTableCell>
                      <RowTableCell>{item.missing_quantity}</RowTableCell>
                      <RowTableCell>
                        {formatConsumptionQuantity(getItemConsumableQuantity(item))}
                      </RowTableCell>
                      <RowTableCell>{formatOrderItemStatus(item.status)}</RowTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>

          {activeConsumption && (
            <Alert severity="info">
              Consumo activo: {activeConsumption.consumption_number} (
              {formatConsumptionStatus(activeConsumption.status)})
            </Alert>
          )}

          {blockedByPendingConsumption && (
            <Alert
              severity="warning"
              action={
                canConfirmBlockingConsumption ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleConfirmBlockingConsumption}
                    disabled={isActionLocked}
                  >
                    Confirmar consumo
                  </Button>
                ) : null
              }
            >
              {canConfirmBlockingConsumption
                ? `Hay material pendiente de consumir, pero el consumo ${blockingConsumption.consumption_number} está en estado Generado. Confirmalo para habilitar un nuevo consumo.`
                : `Hay material pendiente de consumir. Finalizá o revertí el consumo ${blockingConsumption.consumption_number} antes de generar otro.`}
            </Alert>
          )}

          {pendingOrderAuthorization && (
            <Alert severity="info">
              {permissions.canAuthorizeTechnicalOrders
                ? 'El pedido está pendiente de autorización. Usá "Autorizar pedido" para continuar con el control de stock.'
                : 'El pedido está pendiente de autorización del área técnica.'}
            </Alert>
          )}

          {pendingConsumptionAuthorization && (
            <Alert severity="info">
              Hay {readyToConsumeItems.length} material
              {readyToConsumeItems.length === 1 ? '' : 'es'} con stock disponible para consumir.
              {' '}
              {permissions.canAuthorizeConsumption
                ? 'Usá "Autorizar consumo" para habilitar el botón de consumo.'
                : 'Solicitá al área técnica que autorice el consumo para habilitar el botón de consumo.'}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {canAuthorizeOrder && (
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleAuthorizeOrder}
                disabled={isActionLocked}
              >
                Autorizar pedido
              </Button>
            )}
            {canAuthorizeConsumption && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleAuthorizeConsumption}
                disabled={isActionLocked}
              >
                Autorizar consumo
              </Button>
            )}
            {canCheckStock && (
              <Button
                variant="outlined"
                startIcon={<InventoryIcon />}
                onClick={handleCheckStock}
                disabled={isActionLocked}
              >
                Controlar stock
              </Button>
            )}
            {canGenerateConsumption && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ShoppingCartCheckoutIcon />}
                onClick={() => setGenerateDialogOpen(true)}
                disabled={isActionLocked}
              >
                {generateConsumptionLabel}
              </Button>
            )}
            {canGenerateSupplyOrders && (
              <Button
                variant="contained"
                color="info"
                startIcon={<RequestQuoteIcon />}
                onClick={() => setGenerateSupplyDialogOpen(true)}
                disabled={isActionLocked}
              >
                Generar orden a proveedor
              </Button>
            )}
            {canCancelOrder && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setCancelDialogOpen(true)}
                disabled={isActionLocked}
              >
                Cancelar pedido
              </Button>
            )}
          </Box>
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={() => {}}
        saveButtonProps={{ sx: { display: 'none' } }}
        cancelButtonProps={{ disabled: isActionLocked }}
        cancelLabel="Cerrar"
      />

      <GenerateConsumptionDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        order={order}
        warehouses={warehouses}
        defaultWarehouseId={order?.uuid_destination_warehouse}
        articlesByUuid={articlesByUuid}
        onSuccess={handleGenerateSuccess}
        onPendingChange={setNestedActionPending}
      />

      <GenerateSupplyOrdersDialog
        open={generateSupplyDialogOpen}
        onClose={() => setGenerateSupplyDialogOpen(false)}
        externalOrder={order}
        warehouses={warehouses}
        providers={providers}
        onGenerate={handleGenerateSupplyOrders}
        isProcessing={createSupplyOrdersMutation.isPending}
        providerOnly
      />

      <SupplyOrderActionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        title="Cancelar pedido técnico"
        description="Esta acción cancelará el pedido y liberará reservas o traslados abiertos vinculados. Indicá el motivo."
        confirmLabel="Cancelar pedido"
        requireReason
        isProcessing={isProcessing}
        onConfirm={async ({ reason }) => {
          if (!order?.uuid) return;
          await onCancelOrder({ id: order.uuid, reason });
          await onOrderUpdated?.();
          setResultMessage('Pedido técnico cancelado correctamente.');
        }}
      />
    </DialogContainer>
  );
};

export default InternalOrderDetailDialog;
